import { useEffect, useRef, useState } from "react";
import { ArrowRight, Check, Package } from "lucide-react";
import { API } from "@/context/AuthContext";
import axios from "axios";

const categoryLabels = { 
  "coconut-fiber": "Coconut Fiber", 
  "cocopeat-blocks": "Cocopeat Blocks", 
  "coir-pith": "Coir Pith", 
  "other-coir": "Other Coir" 
};

const categoryColors = {
  "coconut-fiber": "from-amber-500 to-orange-500",
  "cocopeat-blocks": "from-green-500 to-emerald-500",
  "coir-pith": "from-teal-500 to-cyan-500",
  "other-coir": "from-purple-500 to-violet-500",
};

export default function Products({ onViewAll }) {
  const ref = useRef(null);
  const [vis, setVis] = useState(false);
  const [products, setProducts] = useState([]);
  const [cat, setCat] = useState("all");

  useEffect(() => {
    axios.get(`${API}/products`).then(r => setProducts(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setVis(true); obs.disconnect(); } }, { threshold: 0.1 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  const filtered = cat === "all" ? products.slice(0, 4) : products.filter(p => p.category === cat).slice(0, 4);
  const categories = ["all", ...new Set(products.map(p => p.category))];

  return (
    <section ref={ref} className="section-padding bg-[#F9FAFB] relative overflow-hidden" data-testid="products-section">
      {/* Decorative background */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-bl from-forest/5 to-transparent rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-gradient-to-tr from-coir/5 to-transparent rounded-full blur-3xl" />
      
      <div className="container-custom relative z-10">
        {/* Header */}
        <div className={`text-center max-w-3xl mx-auto mb-12 transition-all duration-700 ${vis ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="w-12 h-[2px] bg-gradient-to-r from-transparent to-forest" />
            <span className="text-forest font-semibold text-sm uppercase tracking-[0.2em] font-sans">Our Products</span>
            <div className="w-12 h-[2px] bg-gradient-to-l from-transparent to-forest" />
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif text-navy mb-6 tracking-tight">
            Premium <span className="text-forest">Coconut Fiber</span> & <span className="text-coir">Coco Peat</span> Products
          </h2>
          <p className="text-slate-500 text-lg font-sans leading-relaxed">
            High-quality coir products for agricultural and industrial applications, crafted with expertise.
          </p>
        </div>

        {/* Category Filter */}
        <div className={`flex flex-wrap justify-center gap-3 mb-12 transition-all duration-700 delay-200 ${vis ? "opacity-100" : "opacity-0"}`}>
          {categories.map(c => (
            <button 
              key={c} 
              onClick={() => setCat(c)} 
              className={`px-6 py-2.5 text-sm font-medium font-sans transition-all duration-300 ${
                cat === c 
                  ? "bg-navy text-white shadow-lg shadow-navy/20 -translate-y-0.5" 
                  : "bg-white text-slate-600 hover:bg-navy/5 border border-slate-200 hover:border-navy/30"
              }`} 
              data-testid={`filter-${c}`}
            >
              {c === "all" ? "All Products" : categoryLabels[c] || c}
            </button>
          ))}
        </div>

        {/* Products Grid */}
        <div className={`grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12 transition-all duration-700 delay-300 ${vis ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          {filtered.map((product, i) => (
            <ProductCard key={product.id} product={product} index={i} />
          ))}
        </div>

        {/* CTA */}
        <div className="text-center">
          <button 
            onClick={onViewAll} 
            className="btn-primary group flex items-center gap-3 mx-auto" 
            data-testid="view-all-products-btn"
          >
            View All Products
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </button>
        </div>
      </div>
    </section>
  );
}

function ProductCard({ product, index }) {
  const [hovered, setHovered] = useState(false);
  const avColors = { 
    "in-stock": "bg-green-100 text-green-700 border-green-200", 
    "limited": "bg-amber-100 text-amber-700 border-amber-200", 
    "out-of-stock": "bg-red-100 text-red-700 border-red-200" 
  };
  
  const gradient = categoryColors[product.category] || "from-slate-400 to-slate-500";

  return (
    <div 
      className="bg-white shadow-lg shadow-slate-100/50 overflow-hidden hover:shadow-2xl transition-all duration-500 group hover:-translate-y-2 card-3d" 
      onMouseEnter={() => setHovered(true)} 
      onMouseLeave={() => setHovered(false)} 
      data-testid={`product-card-${product.id}`}
      style={{ transitionDelay: `${index * 100}ms` }}
    >
      {/* Image Section */}
      <div className="relative h-52 overflow-hidden bg-slate-100">
        {product.images?.[0] ? (
          <img 
            src={product.images[0]} 
            alt={product.name} 
            className={`w-full h-full object-cover transition-transform duration-700 ${hovered ? "scale-110" : "scale-100"}`} 
          />
        ) : (
          <div className={`absolute inset-0 bg-gradient-to-br ${gradient} opacity-20`} />
        )}
        
        {/* Placeholder content if no image */}
        {!product.images?.[0] && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center p-4">
              <div className={`w-16 h-16 bg-gradient-to-br ${gradient} rounded-2xl flex items-center justify-center mx-auto mb-3 shadow-lg`}>
                <Package className="w-8 h-8 text-white" />
              </div>
              <p className="text-sm text-slate-500 font-sans font-medium">{product.name}</p>
            </div>
          </div>
        )}
        
        {/* Availability Badge */}
        <div className="absolute top-3 right-3">
          <span className={`px-3 py-1 text-xs font-semibold font-sans border ${avColors[product.availability] || "bg-slate-100 text-slate-600 border-slate-200"}`}>
            {(product.availability || "").replace("-", " ")}
          </span>
        </div>
        
        {/* Hover overlay */}
        <div className={`absolute inset-0 bg-gradient-to-t from-navy/60 to-transparent transition-opacity duration-500 ${hovered ? "opacity-100" : "opacity-0"}`} />
      </div>
      
      {/* Content Section */}
      <div className="p-5">
        {/* Category */}
        <p className={`text-xs font-semibold uppercase tracking-widest mb-2 font-sans bg-gradient-to-r ${gradient} bg-clip-text text-transparent`}>
          {(product.category || "").replace(/-/g, " ")}
        </p>
        
        {/* Title */}
        <h3 className="text-lg font-serif text-navy mb-2 group-hover:text-forest transition-colors duration-300 tracking-tight">
          {product.name}
        </h3>
        
        {/* Description */}
        <p className="text-slate-500 text-sm mb-4 line-clamp-2 font-sans leading-relaxed">
          {product.description}
        </p>
        
        {/* Specifications */}
        {product.specifications && (
          <div className="space-y-1.5 mb-4">
            {product.specifications.ph && (
              <div className="flex items-center text-sm font-sans">
                <Check className="w-4 h-4 text-forest mr-2 flex-shrink-0" />
                <span className="text-slate-600">pH: {product.specifications.ph}</span>
              </div>
            )}
            {product.specifications.ec && (
              <div className="flex items-center text-sm font-sans">
                <Check className="w-4 h-4 text-forest mr-2 flex-shrink-0" />
                <span className="text-slate-600">EC: {product.specifications.ec}</span>
              </div>
            )}
          </div>
        )}
        
        {/* Footer */}
        <div className="flex items-center justify-between pt-4 border-t border-slate-100">
          <div>
            <p className="text-xs text-slate-400 font-sans">Min. Order</p>
            <p className="text-sm font-semibold text-navy font-sans">{product.moq}</p>
          </div>
          <button 
            className="text-forest text-sm font-semibold hover:text-coir flex items-center gap-1.5 font-sans group/btn transition-colors duration-300" 
            data-testid={`product-details-${product.id}`}
          >
            Details
            <ArrowRight className="w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
          </button>
        </div>
      </div>
    </div>
  );
}
